import React, { useState } from "react";
import { motion } from "framer-motion";
import { Star, Sparkles, Send } from "lucide-react";
import { testimonials } from "../data/mock"; // On charge vos avis gérés par l'admin !
import { toast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";

// Fonction utilitaire pour envoyer les données à Netlify
const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

const AvisPage = () => {
  const [rating, setRating] = useState(5);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    event: "",
    text: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Envoi à Netlify Forms
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ 
          "form-name": "soumission-avis", 
          ...formData, 
          rating: rating,
          date: new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
        }),
      });
      if (!response.ok) throw new Error("L'avis n'a pas pu être envoyé.");

      toast({
        title: "Merci pour votre avis ! ❤️",
        description: "Il a bien été reçu et sera publié après vérification.",
      });

      // Réinitialisation du formulaire
      setFormData({ name: "", event: "", text: "" });
      setRating(5);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Oups !",
        description: "Une erreur est survenue lors de l'envoi.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#F6F7F1]">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-16 bg-[#D4AF37]" />
            <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            <div className="h-px w-16 bg-[#D4AF37]" />
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Les Avis de nos Clients
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Découvrez vos retours d'expérience ou partagez votre propre moment de douceur avec nous.
          </p>
        </motion.div>

        {/* Grille des avis existants */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {testimonials.length === 0 && (
            <p className="md:col-span-2 lg:col-span-3 text-center text-gray-500">
              Les avis vérifiés apparaîtront ici après validation.
            </p>
          )}
          {testimonials.map((t, index) => {
            const parsedRating = Number(t.rating);
            const displayRating = Number.isFinite(parsedRating)
              ? Math.min(5, Math.max(1, Math.floor(parsedRating)))
              : 5;
            return (
            <motion.div
              key={t.id || index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-white rounded-2xl p-8 relative shadow-sm border border-gray-100 flex flex-col justify-between"
            >
              <div>
                <div className="absolute top-4 right-6 text-6xl font-serif text-[#E5007D]/10 leading-none">"</div>
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: displayRating }, (_, i) => (
                    <Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed text-sm italic">"{t.text}"</p>
              </div>
              
              <div className="flex items-center gap-3 mt-4 border-t border-gray-50 pt-4">
                <div className="w-10 h-10 rounded-full bg-[#E5007D] flex items-center justify-center text-white font-bold text-sm shrink-0">
                  {t.name ? t.name.charAt(0) : "C"}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.event} — {t.date}</p>
                </div>
              </div>
            </motion.div>
            );
          })}
        </div>

        {/* Formulaire de dépôt d'avis (Netlify Forms) */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto bg-white rounded-3xl p-8 shadow-sm border border-gray-100"
        >
          <div className="text-center mb-8">
            <h2 className="font-playfair text-2xl font-bold text-gray-900 mb-2">Vous avez goûté à nos créations ?</h2>
            <p className="text-gray-500 text-sm">Votre message sera vérifié par nos soins avant d'être publié.</p>
          </div>
          
          <form 
            name="soumission-avis" 
            method="POST" 
            data-netlify="true" 
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            {/* Champ invisible pour Netlify */}
            <input type="hidden" name="form-name" value="soumission-avis" />
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Votre Nom / Prénom</label>
                <input 
                  type="text" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  required 
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#E5007D]/20 focus:border-[#E5007D] outline-none" 
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type d'événement (ex: Mariage)</label>
                <input 
                  type="text" 
                  name="event" 
                  value={formData.event}
                  onChange={handleChange}
                  required 
                  className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#E5007D]/20 focus:border-[#E5007D] outline-none" 
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-2">Votre note</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((num) => (
                  <button 
                    type="button" 
                    key={num} 
                    onClick={() => setRating(num)}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star className={`w-6 h-6 ${num <= rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-gray-300"}`} />
                  </button>
                ))}
              </div>
              <input type="hidden" name="rating" value={rating} />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Votre message</label>
              <textarea 
                name="text" 
                value={formData.text}
                onChange={handleChange}
                rows="4" 
                required 
                className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#E5007D]/20 focus:border-[#E5007D] outline-none resize-none"
              ></textarea>
            </div>

            <button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-[#E5007D] text-white py-3 px-6 rounded-xl font-medium text-sm hover:bg-[#c4006b] transition-all duration-300 flex items-center justify-center gap-2 shadow-md shadow-[#E5007D]/20"
            >
              {isSubmitting ? "Envoi..." : <><Send className="w-4 h-4" /> Envoyer mon avis</>}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AvisPage;
